<template>
	<v-form
		v-if="processedSchema && Object.keys(processedSchema).length"
		v-model="valid"
	>
		<v-row v-if="processedSchema.fields && processedSchema.fields.length">
			<!-- Loop fields as columns -->
			<v-col
				v-for="(field, index) in processedSchema.fields"
				:key="index"
				v-bind="field.colProps"
			>
				<!-- Insert field depending on type -->
				<v-text-field
					v-if="field.type == 'text'"
					v-bind="field.props"
					:rules="field.rules"
					v-model="value[field.name]"
					@input="updateValue(field.name, value[field.name])"
					dense
				/>
				<v-checkbox
					v-else-if="field.type == 'checkbox'"
					v-bind="field.props"
					:rules="field.rules"
					v-model="value[field.name]"
					@input="updateValue(field.name, value[field.name])"
					dense
				/>
				<template v-else-if="field.type == 'checkboxes' && field.props.items">
					<h3 v-if="field.props.label">
						{{field.props.label}}
					</h3>
					<v-checkbox
						v-for="(item, index) in field.props.items"
						:key="index"
						v-model="value[field.name]"
						:label="field.props['item-text'] ? item[field.props['item-text']] : item['title']"
						:value="field.props['item-value'] ? item[field.props['item-value']] : item['value']"
						dense
						@input="updateValue(field.name, value[field.name])"
						hide-details
					/>
				</template>
				<v-switch
					v-else-if="field.type == 'switch'"
					v-bind="field.props"
					:rules="field.rules"
					v-model="value[field.name]"
					@input="updateValue(field.name, value[field.name])"
					dense
				/>
				<v-select
					v-else-if="field.type == 'select'"
					v-bind="field.props"
					:rules="field.rules"
					v-model="value[field.name]"
					@input="updateValue(field.name, value[field.name])"
					dense
				/>
				<v-autocomplete
					v-else-if="field.type == 'autocomplete'"
					v-bind="field.props"
					:rules="field.rules"
					v-model="value[field.name]"
					@input="updateValue(field.name, value[field.name])"
					dense
				/>
				<v-dialog
					v-else-if="field.type == 'date'"
					:ref="('dialog_' + field.name)"
					v-model="field.modalOpen"
					:return-value.sync="value[field.name]"
					persistent
					width="290px"
				>
					<template v-slot:activator="{ on }">
						<v-text-field
							v-bind="field.props"
							v-model="field.dateFormatted"
							readonly
							v-on="on"
						></v-text-field>
					</template>
					<v-date-picker
						v-model="value[field.name]"
						scrollable
						v-bind="field.pickerProps"
						locale="fi"
					>
						<v-spacer></v-spacer>
						<v-btn text color="primary" @click="field.modalOpen = false">Peruuta</v-btn>
						<v-btn text color="primary" @click="saveDialog('dialog_' + field.name, value[field.name])">OK</v-btn>
					</v-date-picker>
				</v-dialog>
				<p v-else>
					<strong>Unknown field type: {{field.type}}</strong>
				</p>
			</v-col>
		</v-row>

		<!-- Default slot for submit buttons etc -->
		<slot />

		<!-- Debug -->
		<template v-if="debug">
			<p>
				<strong>Form is valid: </strong>{{valid}}
			</p>
			<p v-if="value">
				<strong>Value:</strong><br />
				{{value}}
			</p>
			<p v-if="processedSchema">
				<strong>Processed schema:</strong><br />
				{{processedSchema}}
			</p>
			<p v-if="schema">
				<strong>Original schema:</strong><br />
				{{processedSchema}}
			</p>
		</template>
	</v-form>
</template>

<script>

import validationRules from '@/utils/validationRules'

export default {
	name: 'SchemaToForm',
	props: {
		// Debug mode
		debug: {
			type: Boolean,
			default: () => {
				return false
			}
		},
		// User defined form schema
		schema: {
			type: Object,
			default: () => {
				return {}
			}
		},
		// Object for field values. Intended to use with v-model.
		value: {
			type: Object,
			default: () => {
				return {}
			}
		},

		// Enable field rules?
		enableRules: {
			type: Boolean,
			default: () => {
				return true
			}
		},
	},
	data: () => ({
		processedSchema: {}, // Processed schema, which we use to render the form
		valid: false, // Is form valid?
	}),
	methods: {
		// Debug logging
		log (text = '') {
			if (!this.debug || !text) return

			console.log('SchemaToForm: ' + text)
		},

		// Debug warning logging
		warn (text = '') {
			if (!this.debug || !text) return

			console.warn('SchemaToForm: ' + text)
		},

		// Since we use object keys as value, we need to use this custom emit to maintain reactivity.
		updateValue (name, val) {
			this.$emit('input', { ...this.value, [name]: val })
		},

		// Parse field validation rules. Actual rules are loaded from validationRules module.
		parseRules (keys = []) {
			if (!this.enableRules) return []
			if (!keys || !keys.length) return []

			return keys.map(key => {
				return validationRules[key] || undefined
			})
		},

		saveDialog (dialog, val) {
			this.$refs[dialog][0].save(val)
		},

		formatDate (val) {
			return new Date(val).toLocaleDateString('fi-FI')
		}
	},
	watch: {
		/*
			 User defined schema may lack some of required properties,
			 so we use this watcher to make sure that schema meets requirements.
		*/
		schema: {
			deep: true,
			immediate: true,
			handler (val) {
				// Copy user defined schema
				let newSchema = JSON.parse(JSON.stringify(val))

				try {
					// Field operations
					if (newSchema.fields && newSchema.fields.length) {
						for (let [index, field] of newSchema.fields.entries()) {
							// Check that field has name defined
							if (!field.type) throw 'Field with index ' + index + ' has no type defined.'

							// Check that field has name defined
							if (!field.name) throw 'Field with index ' + index + ' has no name defined.'

							// Merge default colprops with user defined colprops
							field.colProps = Object.assign({
								cols: 12,
							}, field.colProps)

							// Get field pre-defined value. Form value is prioritized
							if (this.value[field.name]) {
								// In value
								this.$set(this.value, field.name, this.value[field.name])
							} else if (field.value) {
								// In schema
								this.$set(this.value, field.name, field.value)
							}

							// If field has validation rules defined
							field.rules = this.parseRules(field.rules)

							// If field is date field, set formatted value
							if (field.type == 'date') {
								field.dateFormatted = this.formatDate(this.value[field.name])
							}
						}
					} else {
						newSchema.fields = []
					}

					this.processedSchema = newSchema
				} catch (error) {
					this.processedSchema = {}
					console.error('SchemaToForm: ' + error)
				}
			},
		},

		// Emit form field value changes
		value: {
			deep: true,
			handler (val) {
				for (let field of this.processedSchema.fields) {
					// Format date fields
					if (field.type == 'date') {
						this.$set(field, 'dateFormatted', this.formatDate(this.value[field.name]))
					}
				}

				this.$emit('input', val)
			}
		},

		// Emit form validity
		valid (val) {
			return (val) ? this.$emit('valid') : this.$emit('invalid')
		}
	},
}
</script>